import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardBody, Col, Container, Form, FormGroup, Input, Row } from "reactstrap";
import { MetaTags } from "react-meta-tags";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import SfLoadingSkeleton from "components/Common/Loadings/SfLoadingSkeleton";
import BootstrapTable from 'react-bootstrap-table-next';
import { AppConfiguration } from 'config';
import { useDispatch, useSelector } from "react-redux";
import filterFactory from 'react-bootstrap-table2-filter';
import { sanitizeProspectFilters } from "helpers/sf_utils";
import { customSortCaret } from "helpers/bootstrap_table_helper";
import { postAdminUserLogin } from "store/admin/user-login/actions";
import { getAdminIntegrationLogs } from "store/admin/admin-integration-logs/actions";
import CustomProspectFilters from "components/Common/CustomFilters";
import { adminIntegrationLogsColumns, getActiveProspectFilters, mappedFilterItems, onClickOpenFilterModal, onClickRemoveFilter, onCloseFilterModal } from "pages/Admin/AdminLeadIntegrationsLogs/AdminIntegrationLogsFiltersUtils";
import { mapActiveFilters } from "helpers/conversation_filter_helper";
import { ADMIN_INTEGRATION_LOGS_FILTER_SPECIAL_CASES, ADMIN_INTEGRATION_LOGS_FILTERS, DEFAULT_THIS_MONTH_FILTER } from "./admin_lead_integration_logs_helper";
import ShowAppliedFilters from "components/Common/CustomFilters/ShowAppliedFilters";
import { FILTER_COMPONENT_TYPES, REQUIRED_FEATURE_FLAGS } from "helpers/sf_constants";
import AdminUserProfileBreadCrumb from "../Authentication/AdminUserProfileBreadCrumb";
import useFeatureFlags from "hooks/useFeatureFlags";
import OffsetPagination from "components/Common/OffsetPagination";
import { useIntegrationLogsSort } from "hooks/useIntegrationLogsSort";
import { ADMIN_INTEGRATION_LOGS_DEFAULT_SORT_COLUMN, ADMIN_INTEGRATION_LOGS_DEFAULT_SORT_DIRECTION, ADMIN_INTEGRATION_LOGS_SORTABLE_FIELDS, isIntegrationLogsColumnBackendSortable } from "helpers/integration_logs_sort_helpers";
const AdminLeadIntegrationsLogs = () => {
    const dispatch = useDispatch();
    const [isFeatureFlagActive] = useFeatureFlags();

    const { admin_integration_logs, loading, has_next_page, has_lookahead_pages, total_number_of_pages, last_page_fetched } = useSelector((state) => ({
        admin_integration_logs: state.AdminIntegrationLogs.admin_integration_logs,
        loading: state.AdminIntegrationLogs.loading,
        has_next_page: state.AdminIntegrationLogs.has_next_page,
        has_lookahead_pages: state.AdminIntegrationLogs.has_lookahead_pages,
        total_number_of_pages: state.AdminIntegrationLogs.total_number_of_pages,
        last_page_fetched: state.AdminIntegrationLogs.last_page_fetched
    }));
    const [isActiveFilterModal, setIsActiveFilterModal] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [accountNameValue, setAccountNameSearchValue] = useState()
    const [prospectFilterItems, setProspectFilterItems] = useState(DEFAULT_THIS_MONTH_FILTER);

    const filter_items = useMemo(() => {
        const integrationNameOptions = (admin_integration_logs || []).map(integration => ({
            value: integration.id,
            label: integration.name,
        }))
        return mappedFilterItems(integrationNameOptions)
    }, [admin_integration_logs])

    const shouldUseOffsetPagination = useMemo(() => (
        isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_OFFSET_PAGINATION_APPLICATION)
    ), [isFeatureFlagActive]);

    const getSanitizedFiltersPayload = useCallback((prospect_filter_items = []) => {
        if (!prospect_filter_items?.length) {
            return null;
        }
        return mapActiveFilters(sanitizeProspectFilters(prospect_filter_items), ADMIN_INTEGRATION_LOGS_FILTER_SPECIAL_CASES);
    }, []);

    const { sortColumn, sortDirection, backendSortingEnabled, remote, handleTableChange } = useIntegrationLogsSort(
        ADMIN_INTEGRATION_LOGS_DEFAULT_SORT_COLUMN,
        ADMIN_INTEGRATION_LOGS_DEFAULT_SORT_DIRECTION,
        isFeatureFlagActive,
        () => fetchAdminIntegrationLogsPage(1, getSanitizedFiltersPayload(prospectFilterItems))
    );

    // Read the live sort via a ref so fetchAdminIntegrationLogsPage keeps a stable
    // identity: it is a dependency of the initial-load effect, so recreating it on
    // every sort change would re-run that effect and refetch with default filters.
    const sortStateRef = useRef({ sortColumn, sortDirection, backendSortingEnabled });
    sortStateRef.current = { sortColumn, sortDirection, backendSortingEnabled };

    const fetchAdminIntegrationLogsPage = useCallback((pageNumber = 1, filterPayload = null, options = {}) => {
        const queryParams = { page_number: pageNumber };
        if (filterPayload && Object.keys(filterPayload)?.length) {
            queryParams.filters = JSON.stringify(filterPayload);
        }
        const { sortColumn: liveSortColumn, sortDirection: liveSortDirection, backendSortingEnabled: liveBackendSorting } = sortStateRef.current;
        if (liveBackendSorting) {
            queryParams.sort_column = liveSortColumn;
            queryParams.sort_direction = liveSortDirection;
        }

        dispatch(getAdminIntegrationLogs({
            queryParams,
            appendResults: Boolean(options.appendResults),
            useCache: shouldUseOffsetPagination,
        }));
    }, [dispatch, shouldUseOffsetPagination]);

    const onClickLoadMoreData = (prospect_filter_items = null, filterCount = null, last_page_fetched = null) => {
        if (loading) {
            return;
        }
        const activeFilteredColumns = filterCount ? getSanitizedFiltersPayload(prospect_filter_items) : null;
        const nextPage = (last_page_fetched || 0) + 1;
        fetchAdminIntegrationLogsPage(nextPage, activeFilteredColumns, { appendResults: true });
    }

    const onSubmitOpenFilterModal = (values) => {
        setAccountNameSearchValue('')
        setIsActiveFilterModal(true);
        const activeFilteredColumns = sanitizeProspectFilters(values)
        setProspectFilterItems(activeFilteredColumns);
        if (activeFilteredColumns?.length) {
            fetchAdminIntegrationLogsPage(1, getSanitizedFiltersPayload(activeFilteredColumns));
            onCloseFilterModal(setIsActiveFilterModal, setShowFilterModal);
            return
        }
        fetchAdminIntegrationLogsPage(1)
        onCloseFilterModal(setIsActiveFilterModal, setShowFilterModal);
    }

    useEffect(() => {
        const defaultFilters = mapActiveFilters(DEFAULT_THIS_MONTH_FILTER, ADMIN_INTEGRATION_LOGS_FILTER_SPECIAL_CASES);
        fetchAdminIntegrationLogsPage(1, defaultFilters);
    }, [fetchAdminIntegrationLogsPage]);

    const onClickUserLogin = (user) => {
        dispatch(postAdminUserLogin({ user: { account: user.account_id }, history: history }))
    }

    const onClickUserRedirection = (integration) => {
        const redirectionUrl = `/admin_integration_logs/${integration.id}?account_id=${integration.account_id}`;
        window.open(redirectionUrl, "_blank");
    }

    const baseColumns = adminIntegrationLogsColumns(onClickUserLogin, onClickUserRedirection);
    // When backend sorting is on, only offer a sort handle the server can honor.
    const columns = backendSortingEnabled
        ? baseColumns.map((column) => ({
            ...column,
            sort: isIntegrationLogsColumnBackendSortable(column.dataField, ADMIN_INTEGRATION_LOGS_SORTABLE_FIELDS),
        }))
        : baseColumns;

    const { filterCount, activeProspectFilterItems } = useMemo(() => {
        return getActiveProspectFilters(prospectFilterItems)
    }, [prospectFilterItems])

    const getSearchInputFilter = useCallback((value) => {
        const searchFilter = {
            value: value,
            is_active: true,
            component_type: FILTER_COMPONENT_TYPES.TEXT,
            name: ADMIN_INTEGRATION_LOGS_FILTERS.ACCOUNT_NAME
        }

        return searchFilter
    }, [])

    const handleSearchInputSearch = useCallback((value) => {
        const searchFilters = [...prospectFilterItems, getSearchInputFilter(value)];
        fetchAdminIntegrationLogsPage(1, getSanitizedFiltersPayload(searchFilters));
    }, [fetchAdminIntegrationLogsPage, prospectFilterItems, getSanitizedFiltersPayload, getSearchInputFilter])

    const currentPage = Math.max(1, Number(last_page_fetched) || 1);
    const knownTotalPages = Math.max(Number(total_number_of_pages) || 0, currentPage);
    const paginationTotal = knownTotalPages > 0
        ? knownTotalPages
        : (has_next_page
            ? currentPage + (has_lookahead_pages ? 8 : 1)
            : currentPage);

    return (
        <React.Fragment>
            <div className="page-content" id="page-content-wrapper">
                <MetaTags>
                    <title> Lead Integrations Logs | {AppConfiguration.WEBSITE}</title>
                </MetaTags>
                <CustomProspectFilters
                    isActive={isActiveFilterModal}
                    filterItems={prospectFilterItems}
                    onSubmitModalState={onSubmitOpenFilterModal}
                    onClickModalClose={() => onCloseFilterModal(setIsActiveFilterModal, setShowFilterModal)}
                    isActiveSearchBox={false}
                    customMarginTop='sfpro-filter-modal-margin-top'
                />
                <Container fluid>
                    <AdminUserProfileBreadCrumb title="Admin" breadcrumbItem="Lead Integrations Logs" />
                    <Row>
                        <Col md={12}>
                            <Card>
                                <CardBody>
                                    <ToolkitProvider
                                        keyField="id"
                                        columns={columns}
                                        data={admin_integration_logs || []}
                                        exportCSV={{
                                            fileName: "IntegrationsLogs.csv",
                                            exportAll: false,
                                        }}
                                    >
                                        {(toolkitProps) => {
                                            return (
                                            <>
                                                <Form>
                                                    <Row className="d-flex align-items-end">
                                                        <Col md="auto" className="align-self-end">
                                                            <FormGroup className="mt-1 mb-0">
                                                                <div>
                                                                    <button
                                                                        className={`d-flex align-items-center btn btn-outline-primary ${filterCount === 0 ? 'filter-btn-w' : 'filter-btn'}`}
                                                                        type='button'
                                                                        onClick={() => {
                                                                            onClickOpenFilterModal(setIsActiveFilterModal, setShowFilterModal, setProspectFilterItems, filter_items);
                                                                        }}
                                                                    >
                                                                        <i className="bx bx-filter font-size-18 align-middle me-2" />
                                                                        Filters {filterCount != 0 && <span className="numberCircle border-0"> {filterCount} </span>}
                                                                    </button>
                                                                </div>
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md="auto">
                                                            <FormGroup>
                                                                <div className="search-box me-2 d-inline-block">
                                                                    <div className="position-relative">
                                                                        <Input
                                                                            className="rounded"
                                                                            value={accountNameValue}
                                                                            placeholder="Search Account Name"
                                                                            style={{ minWidth: '210px' }}
                                                                            onChange={(e) => {
                                                                                setAccountNameSearchValue(e.target.value);
                                                                                if (e.target.value === '') {
                                                                                    handleSearchInputSearch(e.target.value)
                                                                                }
                                                                            }}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter') {
                                                                                    e.preventDefault()
                                                                                    handleSearchInputSearch(e.target.value)
                                                                                }
                                                                            }}
                                                                        />
                                                                        <i className="bx bx-search-alt search-icon" />
                                                                    </div>
                                                                </div>
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md="7" className="flex-nowrap mb-3" style={{ maxHeight: '100px' }}>
                                                            <ShowAppliedFilters
                                                                filtered_columns={activeProspectFilterItems}
                                                                onClickRemoveFilter={(field_name) => onClickRemoveFilter(field_name, setProspectFilterItems, fetchAdminIntegrationLogsPage)}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </Form>
                                                <Row>
                                                    <Col xl="12">
                                                        <div className="sfTable-Container">
                                                            {loading ? (<SfLoadingSkeleton rows={8} cols={6} />) :
                                                                (<BootstrapTable
                                                                    keyField="id"
                                                                    columns={columns}
                                                                    data={admin_integration_logs}
                                                                    responsive
                                                                    bordered={false}
                                                                    striped={false}
                                                                    rowClasses="tr-hover"
                                                                    filter={filterFactory()}
                                                                    headerClasses="sftableHeader text-black thead-h50"
                                                                    classes="table align-middle table-nowrap text-black"
                                                                    headerWrapperClasses="thead-light"
                                                                    noDataIndication={`You currently don't have any logs`}
                                                                    defaultSorted={[{ dataField: sortColumn, order: sortDirection }]}
                                                                    sort={{ sortCaret: customSortCaret }}
                                                                    remote={remote}
                                                                    onTableChange={handleTableChange}
                                                                    {...toolkitProps.baseProps}
                                                                />)}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </>
                                        )}}
                                    </ToolkitProvider>
                                    <Col xl="12">
                                        {shouldUseOffsetPagination ? (
                                            <OffsetPagination
                                                currentPage={currentPage}
                                                totalPages={paginationTotal}
                                                hasNextPage={has_next_page}
                                                hasDeepOffsetPages={has_lookahead_pages}
                                                offsetLookAheadPages={8}
                                                loading={loading}
                                                onPageChange={(pageNumber) => fetchAdminIntegrationLogsPage(pageNumber, getSanitizedFiltersPayload(prospectFilterItems))}
                                                className="campaign-analysis-pagination mt-3 align-items-center justify-content-end"
                                            />
                                        ) : (
                                            <div className="float-end">
                                                <p className="mx-3">
                                                    {has_next_page && (
                                                        <a
                                                            className='btn btn-outline-sfblue'
                                                            onClick={() => onClickLoadMoreData(prospectFilterItems, filterCount, last_page_fetched)}
                                                        >
                                                            {loading ? 'Please wait...' : 'Load More'}
                                                        </a>
                                                    )}
                                                </p>
                                            </div>
                                        )}
                                    </Col>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    {/* Table ends */}
                </Container>
            </div>
        </React.Fragment>
    )
}

export default AdminLeadIntegrationsLogs